import React, { useEffect, useRef, useMemo, useState } from "react";
import { SET_USER_CHOICES, SET_ITEM_CHOICES } from "../store/actionTypes";
import { useMenuContext } from "../store/order_context";
import { useUserContext } from "../store/user_context";
import swal from 'sweetalert';

export default function OrderMenu() {
  const { 
    userChoices, 
    itemChoices, 
    dispatch, 
    placeOrder, 
    replaceOrder,
    cancelOrder,
    clearCart ,
    userOrder,
  } = useMenuContext();

  const stickyRef = useRef(null);
  const { user } = useUserContext(); 

  // Compute original choices derived from userOrder (if any)
  const originalChoices = useMemo(() => {
    if (!userOrder || !userOrder.orderDetails) return [];
    return userOrder.orderDetails.flatMap(order =>
      order.mealsDetails.map(details => ({
        amount: details.count,
        itemID: details.itemId,
        itemName: details.name,
        itemPrice: details.price,
        mainCategoryName: order.resturantName
      }))
    );
  }, [userOrder]);

  // Compare two choice arrays by itemID and amount
  const areChoicesEqual = (a = [], b = []) => {
    if (a.length !== b.length) return false;
    const mapA = a.reduce((m, it) => { m[it.itemID] = it.amount; return m; }, {});
    const mapB = b.reduce((m, it) => { m[it.itemID] = it.amount; return m; }, {});
    const keysA = Object.keys(mapA);
    for (const k of keysA) {
      if ((mapB[k] || 0) !== mapA[k]) return false;
    }
    return true;
  };

  // Whether there is at least one selected item in current choices
  const hasSelectedItem = useMemo(() => {
    return Array.isArray(userChoices) && userChoices.some(c => (c.amount || 0) > 0);
  }, [userChoices]);

  // Whether the current choices changed compared to the original loaded order
  const isChanged = useMemo(() => {
    if (!userOrder) return false;
    return !areChoicesEqual(originalChoices, userChoices || []);
  }, [originalChoices, userChoices, userOrder]);

  const handlePlaceOrder = async () => {
    if (!user?.userId) {
      console.log('User not found. Please login again.');
      return;
    }
    const result = await placeOrder(user.userId);
    if (result.success) {
      swal({
        title: 'نجاح',
        text: 'تم حفظ الطلب بنجاح',
        icon: 'success',
        buttons: { confirm: { text: 'موافق', className: 'swal-button--confirm' } }
      });
    } else {
      swal({
        title: 'خطأ',
        text: `فشل حفظ الطلب: ${result.error}`,
        icon: 'error',
        buttons: { confirm: { text: 'موافق', className: 'swal-button--confirm' } }
      });
    }
  };

  const handleEditOrder = async () => {
    if (!user?.userId) {
      console.log('User not found. Please login again.');
      return;
    }
    const result = await replaceOrder(user.userId, userOrder?.orderId);
    if (result.success) {
      swal({
        title: 'نجاح',
        text: 'تم تعديل الطلب بنجاح',
        icon: 'success',
        buttons: { confirm: { text: 'موافق', className: 'swal-button--confirm' } }
      });
    } else {
      swal({
        title: 'خطأ',
        text: `فشل تعديل الطلب: ${result.error}`,
        icon: 'error',
        buttons: { confirm: { text: 'موافق', className: 'swal-button--confirm' } }
      });
    }
  };

 
 const handleCancelOrder = async () => {
    if (!user?.userId) {
      console.log('User not found. Please login again.');
      return;
    }
    // Show confirmation dialog
const willDelete = await swal({
  title: 'تأكيد',
  text: 'هل أنت متأكد من إلغاء الطلب؟',
  icon: 'warning',
  buttons: {
    cancel: {
      text: 'تراجع',
      value: false,
      visible: true,
      className: 'swal-button--cancel',
      closeModal: true,
    },
    confirm: {
      text: 'تأكيد',
      value: true,
      visible: true,
      className: 'swal-button--confirm',
      closeModal: true,
    }
  },
  dangerMode: true,
   className: 'cancel-order-popup', 
});

    if (!willDelete) return;
    const result = await cancelOrder(userOrder?.orderId);
    if (result?.success) {
      swal({
        title: 'نجاح',
        text: 'تم إلغاء الطلب بنجاح',
        icon: 'success',
        buttons: { confirm: { text: 'موافق', className: 'swal-button--confirm' } }
      }); 
      deleteAllOrders();
    } else {
      swal({
        title: 'خطأ',
        text: `فشل إلغاء الطلب`,
        icon: 'error',
        buttons: { confirm: { text: 'موافق', className: 'swal-button--confirm' } }
      });
    }
  };

  useEffect(() => {
    let intervalId = null;

    const elementFixed = () => {
      if (intervalId) clearInterval(intervalId);

      intervalId = setInterval(() => {
        if (!stickyRef.current) return;

        const windowHeight = window.innerHeight;
        const elementHeight = stickyRef.current.offsetHeight;
        const elementPos = stickyRef.current.parentNode?.offsetTop || 0;
        const scrollTop = window.scrollY;

        if (elementHeight < windowHeight) {
          if (scrollTop > elementPos) {
            stickyRef.current.classList.add("scrolling");
            stickyRef.current.style.top =
              window.innerWidth <= 768
                ? `${scrollTop - elementPos}px`
                : `${scrollTop - elementPos + 30}px`;
          } else {
            stickyRef.current.classList.remove("scrolling");
            stickyRef.current.style.top = `0px`;
          }
        } else {
          stickyRef.current.style.top = `0px`;
        }
      }, 500);
    };

    const handleScroll = () => {
      if (stickyRef.current) elementFixed();
    };

    if (stickyRef.current) {
      elementFixed();
      window.addEventListener("scroll", handleScroll);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  async function decrement(item) {
    // If this removal results in an empty cart and there's an existing userOrder, 
    if (userChoices.length == 1 && userChoices[0].amount==1 &&userOrder) {
      await handleCancelOrder();
      return;
    }
    dispatch({
      type: SET_ITEM_CHOICES,
      payload: {
        ...itemChoices,
        [item.itemID]: {
          ...itemChoices[item.itemID],
          amount: Math.max((itemChoices[item.itemID]?.amount || 0) - 1, 0),
        },
      },
    });
 
    const updatedUserChoices = [...userChoices];
    const itemIndex = updatedUserChoices.findIndex(
      (orderItem) => orderItem.itemID === item.itemID
    );
 
    if (itemIndex >= 0) {
      if (updatedUserChoices[itemIndex].amount > 1) {
        updatedUserChoices[itemIndex].amount -= 1;
      } else {
        updatedUserChoices.splice(itemIndex, 1);
      }
    }

    dispatch({
      type: SET_USER_CHOICES,
      payload: updatedUserChoices,
    });
  }

  const deleteAllOrders = () => {
    clearCart();
  };

  const groupedOrders = useMemo(() => {
    return userChoices.reduce((acc, order) => {
      if (!acc[order.mainCategoryName]) {
        acc[order.mainCategoryName] = [];
      }
      acc[order.mainCategoryName].push(order);
      return acc;
    }, {});
  }, [userChoices]);

  const totalPrice = useMemo(() => {
    return userChoices.reduce(
      (total, order) => total + order.itemPrice * order.amount,
      0
    );
  }, [userChoices]);

  return (
    <aside className="sticky" ref={stickyRef}>
      <div className="ordered_items">
        <h2>قائمة الطلبات</h2>
        {userChoices && userChoices.length > 0 ? (
          <>
            {Object.keys(groupedOrders).map((categoryName, index) => (
              <div className="restaurant" key={index}>
                <h3>{categoryName}</h3>
                <ul>
                  {groupedOrders[categoryName].map((order, orderIndex) => (
                    <li key={orderIndex}>
                      <span>{order.amount}</span>
                      <p>{order.itemName}</p>
                      <span className="price">
                        {order.itemPrice * order.amount}
                      </span>
                      <button
                        type="button"
                        onClick={() => decrement(order)}
                      >
                        <i className="mIcon">&#xf136;</i>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div className="total">
              <p>الإجمالى</p>
              <span className="price">{totalPrice}</span> 
              <button type="button" onClick={!userOrder && hasSelectedItem?deleteAllOrders:handleCancelOrder} id="deleteAll">
                <i className="mIcon">&#xf136;</i>
              </button>
               
            </div>
            <div className="action group">

            
            {!userOrder && hasSelectedItem && (
               <button
                  type="button"
                  className="btn btn-success"
                  onClick={handlePlaceOrder}
                >
                  حفظ الطلب
                </button>
            )}

          
            {userOrder && isChanged && (
               <button type="button" className="btn btn-primary" onClick={handleEditOrder}>تعديل الطلب</button>
            )}

           
            {userOrder && (
                 <button
                type="button"
                className="btn btn-danger"
                onClick={handleCancelOrder}
              >
                الغاء الطلب
              </button>
            )}
            </div>
          </>
        ) : (
          <div className="no_order">لم يتم اضافة اصناف للقائمة</div>
        )}
      </div>
    </aside>
  );
}
